/**
 * CollectionMethod Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 * This layer can use toast, store updates, and other UI concerns
 */
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { toast } from 'sonner';
import { useCollectionMethodStore } from '@/store/collection-method.store';
import { useTenantStore } from '@/store/tenant.store';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';

export class CollectionMethodIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch collection methods and update store
     */
    async fetchCollectionMethods(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useCollectionMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setMethods(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || page,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || 0,
                    itemsPerPage: pagination.pageSize || pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create collection method
     */
    async createCollectionMethod(data) {
        const store = useCollectionMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create({
                ...data,
                tenantId
            });

            toast.success(i18next.t('integrationToasts:collectionMethodCreatedSuccessfully', { defaultValue: 'Collection method created successfully' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update collection method
     */
    async updateCollectionMethod(id, data) {
        const store = useCollectionMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('integrationToasts:collectionMethodUpdatedSuccessfully', { defaultValue: 'Collection method updated successfully' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete collection method
     */
    async deleteCollectionMethod(id) {
        const store = useCollectionMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.delete(id);

            toast.success(i18next.t('integrationToasts:collectionMethodDeletedSuccessfully', { defaultValue: 'Collection method deleted successfully' }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete collection methods
     * @param {Array<string>} ids
     */
    async bulkDeleteCollectionMethods(ids) {
        const store = useCollectionMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('integrationToasts:nCollectionMethodsDeletedSuccessfully', {
                count: ids.length,
                defaultValue: `${ids.length} collection methods deleted successfully`,
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch dropdown parameters
     */
    async fetchDropdownData() {
        try {
            return await fetchDropdownOptionsFromReference('collectionMethod', async () => {
                const response = await this.service.getDropdownParams();
                return { success: true, data: response.data || [] };
            });
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Collection method not found',
        });
    }
}
